import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Grid, makeStyles, Tab, Tabs } from '@material-ui/core';

import { FormattedMessage, injectIntl } from 'react-intl';

import { Form, Formik } from 'formik';
import { FormServiceContext } from 'app/utils/contexts';

import Documentation from 'app/components/Documentation';
import AlertMessage from 'app/components/AlertMessage';
import SaveButton from 'app/components/SaveButton';
import FinalizeButton from 'app/components/FinalizeButton';
import RequestTabContent from './components/RequestTabContent';
import ScheduleTab from './components/ScheduleTab';

import { useDispatch, useSelector } from 'react-redux';
import notification from 'app/config/notification';

import styles from './styles';
import { SERVICES_CODES, SERVICES_GROUPS } from 'app/config/services';
import {
    clearAllLines,
    clearLineScheduleBoard,
    clearPermissionaries,
    clearSchedulesStore,
    clearSelectedEquipmentClassification,
    clearSelectedEquipmentType,
    clearSelectedLineId,
    clearSelectedLinesRequest,
    clearSelectedSubstituteEquipmentClassification,
    clearSelectedSubstituteEquipmentType,
    fetchAllLines,
    setAllLines,
    setLineScheduleBoard,
    setPermissionaries,
    setSchedulesStore,
    setSelectedEquipmentClassification,
    setSelectedEquipmentType,
    setSelectedLineId,
    setSelectedLinesRequest,
    setSelectedSubstituteEquipmentClassification,
    setSelectedSubstituteEquipmentType,
} from 'app/store/slices/line';
import {
    clearVehicleClassification,
    clearVehicleTypes,
    fetchVehicleClassification,
    fetchVehicleTypes,
} from '../../../store/slices/vehicle';
import lineEndpoint from 'app/api/services/lineEndpoint';
import companyEndpoints from '../../../api/services/companyEndpoints';

const useStyles = makeStyles(styles);

function LinesRegistration(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const viewsIdentification = 'req';
    const viewsScheduleBoard = 'sched';
    const viewsDocumentation = 'doc';

    const withScheduleBoard = [
        SERVICES_CODES.SLIC_MODIFICACAO_HORARIOS,
        SERVICES_CODES.SLIC_AMPLIACAO_HORARIOS,
        SERVICES_CODES.SLIC_SUPRESSAO_HORARIOS,
        SERVICES_CODES.STRIP_PROLONGAMENTO_LINHA,
        SERVICES_CODES.STRIP_ENCURTAMENTO_LINHA,
        SERVICES_CODES.STRIP_REFORCO_HORARIOS,
        SERVICES_CODES.STRIP_CONEXAO_LINHAS,
        SERVICES_CODES.STRIP_ALTERACAO_DEFINITIVA_ITINERARIO,
        SERVICES_CODES.STRIP_ALTERACAO_PROVISORIA_ITINERARIO,
        SERVICES_CODES.STRIP_INCLUSAO_TIPO_EQUIPAMENTO,
        SERVICES_CODES.STRIP_SUBSTITUICAO_TIPO_EQUIPAMENTO,
        SERVICES_CODES.STRIP_MODIFICACAO_HORARIOS,
        SERVICES_CODES.STRIP_AMPLIACAO_HORARIOS,
        SERVICES_CODES.STRIP_SUPRESSAO_HORARIOS,
    ];

    const initWithBlankSchedules = [
        SERVICES_CODES.STRIP_PROLONGAMENTO_LINHA,
        SERVICES_CODES.STRIP_ENCURTAMENTO_LINHA,
        SERVICES_CODES.STRIP_REFORCO_HORARIOS,
        SERVICES_CODES.STRIP_CONEXAO_LINHAS,
        SERVICES_CODES.STRIP_ALTERACAO_DEFINITIVA_ITINERARIO,
        SERVICES_CODES.STRIP_ALTERACAO_PROVISORIA_ITINERARIO,
        SERVICES_CODES.STRIP_INCLUSAO_TIPO_EQUIPAMENTO,
        SERVICES_CODES.STRIP_SUBSTITUICAO_TIPO_EQUIPAMENTO,
    ];

    const [view, setView] = useState(viewsIdentification);
    const [loadingChecks, setLoadingChecks] = useState({
        loadingPermissionaryData: true,
        loadingVehicles: true,
    });
    const [loading, setLoading] = useState(true);
    const [hasLine, setHasLine] = useState(false);
    const [companyData, setCompanyData] = useState(null);
    const [permissionaryData, setPermissionaryData] = useState(null);

    const contextProps = useContext(FormServiceContext);

    const service = useSelector((state) => state.services.details);
    const allLines = useSelector((state) => state.line.allLines);
    const selectedLineId = useSelector((state) => state.line.selectedLineId);
    const user = useSelector((state) => state.auth.user);
    const userAdmin = useSelector((state) => state.auth.userAdmin);
    const registrationUserInfo = useSelector((state) => state.auth.registrationInfo);
    const activeCompany = useSelector((state) => state.company.activeCompany);
    const internalNewRequest = useSelector((state) => state.forms.internalNewRequest);
    const vehicleType = useSelector((state) => state.vehicle.vehicleType);
    const vehicleClassification = useSelector((state) => state.vehicle.vehicleClassification);
    const request = useSelector((state) => state.requests.details);

    const requestJsonData = useMemo(() => {
        return request.jsonSolicitacao ? JSON.parse(request.jsonSolicitacao) : {};
    }, [request]);

    const selectedLinesFromRequest = useMemo(() => {
        if (requestJsonData?.linhasSolicitacao) {
            return requestJsonData.linhasSolicitacao;
        }
        return [];
    }, [requestJsonData]);

    const isStripContext = [...SERVICES_GROUPS.LINES_STRIP].includes(service.cdServico);

    const {
        initialValues,
        validationSchema,
        validateOnSave,
        validateForm,
        onSubmit,
        onSave,
        onCancel,
        mainColor,
        hideActionBtn,
        hideFinalizeBtn,
        canEdit,
        editing,
        isInternalForm,
        loadingDocuments,
    } = useContext(FormServiceContext);

    useEffect(() => {
        (async () => {
            const promisses = [];
            if (vehicleType.length === 0) promisses.push(dispatch(fetchVehicleTypes()));
            if (vehicleClassification.length === 0) promisses.push(dispatch(fetchVehicleClassification()));
            if (promisses.length > 0) await Promise.all(promisses);
            setLoadingChecks((prev) => ({ ...prev, loadingVehicles: false }));
        })();
        return () => {
            dispatch(clearVehicleTypes());
            dispatch(clearVehicleClassification());
            dispatch(clearSelectedEquipmentType());
            dispatch(clearSelectedEquipmentClassification());
            dispatch(clearSelectedSubstituteEquipmentType());
            dispatch(clearSelectedSubstituteEquipmentClassification());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        (async () => {
            const filter = {};
            if (!isInternalForm && canEdit) {
                if (Object.keys(internalNewRequest).length > 0) {
                    const doc = internalNewRequest.company.deCnpjMatriz
                        ? internalNewRequest.company.deCnpjMatriz
                        : internalNewRequest.company.deCpf;
                    filter.deCpfCnpj = doc;
                } else if (request.cdUsuarioSolicitacao) {
                    filter.deCpfCnpj = request.cdUsuarioSolicitacao;
                }
                filter.cdServico = service.cdServico;
                if (allLines.length === 0) await dispatch(fetchAllLines(filter, isStripContext && editing));
            } else {
                const linesToRequest = [];
                if (requestJsonData?.linha) {
                    linesToRequest.push(requestJsonData.linha);
                    dispatch(setSelectedLineId(requestJsonData.linha.cdLinha));
                }

                if (requestJsonData?.linhasSolicitacao) {
                    linesToRequest.push(...requestJsonData.linhasSolicitacao);
                    dispatch(setSelectedLinesRequest(requestJsonData.linhasSolicitacao));
                }

                if (linesToRequest.length > 0) {
                    dispatch(setAllLines(linesToRequest));
                }
            }
        })();
        return () => {
            dispatch(clearSchedulesStore());
            dispatch(clearPermissionaries());
            dispatch(clearLineScheduleBoard());
            dispatch(clearAllLines());
            dispatch(clearSelectedLineId());
            dispatch(clearSelectedLinesRequest());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            if (!selectedLineId) {
                setLoadingChecks((prev) => ({ ...prev, loadingPermissionaryData: false }));
                return;
            }

            setHasLine(true);
            if (withScheduleBoard.includes(service.cdServico)) {
                let tempScheduleBoard = { horarios: [] };
                let initialScheduleBoard = initialValues?.quadroHorarios;
                if (editing && initialScheduleBoard && Object.keys(initialScheduleBoard).length > 0) {
                    tempScheduleBoard = { ...initialValues.quadroHorarios };
                } else {
                    if (!initWithBlankSchedules.includes(service.cdServico)) {
                        const response = await lineEndpoint.getScheduleBoard(selectedLineId);
                        if (response && response.status === 200 && response.data && response.data.length > 0) {
                            tempScheduleBoard = response.data[0];
                        }
                    }
                }
                dispatch(setLineScheduleBoard(tempScheduleBoard));
                dispatch(setSchedulesStore(tempScheduleBoard.horarios || []));
            }

            if (!isStripContext) {
                const response = await lineEndpoint.getLinePermissionaries(selectedLineId);
                if (response && response.status === 200) {
                    let permissionariesResponseData = response.data;
                    dispatch(setPermissionaries(permissionariesResponseData));
                    let doc = initialValues.deCpfCnpj || null;
                    if (Object.keys(internalNewRequest).length > 0) {
                        doc = internalNewRequest.company.deCnpjMatriz
                            ? internalNewRequest.company.deCnpjMatriz
                            : internalNewRequest.company.deCpf;
                    } else if (!isInternalForm) {
                        if (activeCompany && Object.keys(activeCompany).length > 0) {
                            doc = activeCompany.deCnpjMatriz ? activeCompany.deCnpjMatriz : activeCompany.deCpf;
                        } else if (user.cdUsuarioAdmin) {
                            doc = user.cdUsuarioAdmin;
                        } else if (registrationUserInfo && Object.keys(registrationUserInfo).length > 0) {
                            doc = registrationUserInfo.cdUsuarioAdmin
                                ? registrationUserInfo.cdUsuarioAdmin
                                : registrationUserInfo.cdUsuario;
                        }
                    }
                    let filtered = permissionariesResponseData.filter((p) => {
                        return p.deCpfCnpj === doc;
                    });
                    setPermissionaryData(filtered.length > 0 ? filtered[0] : null);
                }
            }
            setLoadingChecks((prev) => ({ ...prev, loadingPermissionaryData: false }));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues, selectedLineId]);

    useEffect(() => {
        (async () => {
            if (!request.cdSolicitacao) {
                if (internalNewRequest && internalNewRequest.company) {
                    setCompanyData(internalNewRequest.company);
                } else if (activeCompany && Object.keys(activeCompany).length > 0) {
                    setCompanyData(activeCompany);
                } else if (user.cdUsuarioAdmin) {
                    setCompanyData(userAdmin);
                } else if (registrationUserInfo && Object.keys(registrationUserInfo).length > 0) {
                    setCompanyData(registrationUserInfo);
                }
            } else {
                if (!companyData && request.cdEmpresa) {
                    const response = await companyEndpoints.getCompany({ cdEmpresa: request.cdEmpresa });
                    if (response && response.status === 200 && response.data) {
                        setCompanyData(response.data);
                    }
                } else {
                    const json = JSON.parse(request.jsonSolicitacao);
                    setCompanyData(json);
                }
            }
        })();
        // eslint-disable-next-line
    }, [activeCompany, internalNewRequest, registrationUserInfo, request, user, userAdmin]);

    useEffect(() => {
        if (!isStripContext) return;

        if (editing) {
            if (initialValues.cdLinha && (!selectedLineId || selectedLineId === '')) {
                dispatch(setSelectedLineId(initialValues.cdLinha));
            }
            if (!!selectedLinesFromRequest.length) {
                dispatch(setSelectedLinesRequest(initialValues.linhasSolicitacao));
            }
            if (initialValues.tipoVeiculo && Object.keys(initialValues.tipoVeiculo).length > 0) {
                dispatch(setSelectedEquipmentType(initialValues.tipoVeiculo));
            }
            if (initialValues.categoriaVeiculo && Object.keys(initialValues.categoriaVeiculo).length > 0) {
                dispatch(setSelectedEquipmentClassification(initialValues.categoriaVeiculo));
            }
            if (initialValues.tipoVeiculoSubst && Object.keys(initialValues.tipoVeiculoSubst).length > 0) {
                dispatch(setSelectedSubstituteEquipmentType(initialValues.tipoVeiculoSubst));
            }
            if (initialValues.categoriaVeiculoSubst && Object.keys(initialValues.categoriaVeiculoSubst).length > 0) {
                dispatch(setSelectedSubstituteEquipmentClassification(initialValues.categoriaVeiculoSubst));
            }
        } else {
            let isStripEquipmentSubst = SERVICES_CODES.STRIP_SUBSTITUICAO_TIPO_EQUIPAMENTO === service.cdServico;

            if (isStripEquipmentSubst) {
                const selectedLine = allLines.find((line) => line.cdLinha === selectedLineId);
                if (selectedLine) {
                    const selType = vehicleType.find((tipo) => tipo.cdTipoVeiculo === selectedLine.cdTipoVeiculo);
                    const selClassif = vehicleClassification.find(
                        (classificacao) => classificacao.cdClassificacaoVeiculo === selectedLine.cdCategoria,
                    );
                    dispatch(setSelectedEquipmentType(selType));
                    dispatch(setSelectedEquipmentClassification(selClassif));
                }
            }
        } // eslint-disable-next-line
    }, [allLines, vehicleType, vehicleClassification, selectedLineId]);

    useEffect(() => {
        const isLoading = Object.values(loadingChecks).filter((isLoading) => isLoading).length > 0;
        setLoading(isLoading);
    }, [loadingChecks]);

    const handleViewChange = (event, value) => {
        setView(value);
    };

    const renderTabs = () => {
        return (
            <Grid container spacing={3} className={classes.tabsContainer}>
                <Tabs
                    variant="scrollable"
                    onChange={handleViewChange}
                    className={classes.tabs}
                    indicatorColor={mainColor ? mainColor : 'primary'}
                    textColor={mainColor ? mainColor : 'primary'}
                    value={view}>
                    <Tab
                        className={classes.tab}
                        label={<FormattedMessage id="LABELS.SERVICES.REQUEST" />}
                        value="req"
                    />

                    {withScheduleBoard.includes(service.cdServico) && hasLine && (
                        <Tab
                            className={classes.tab}
                            label={<FormattedMessage id="LABELS.SERVICES.SCHEDULE_BOARD" />}
                            value="sched"
                        />
                    )}
                    {loadingDocuments && !isInternalForm && (
                        <Tab
                            className={classes.tab}
                            label={<CircularProgress size={12} className={classes.loading} />}
                            value="doc"
                        />
                    )}
                    {!isInternalForm && !loadingDocuments && hasLine && (
                        <Tab
                            className={classes.tab}
                            label={<FormattedMessage id="LABELS.COMMONS.DOCUMENTATION" />}
                            value="doc"
                        />
                    )}
                </Tabs>
            </Grid>
        );
    };

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={initialValues ? initialValues : {}}
                validationSchema={validationSchema}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={(values) => onSubmit(values)}>
                {(formProps) => {
                    return (
                        <Form className={classes.containerForm}>
                            {renderTabs()}
                            <AlertMessage isInternalForm={isInternalForm} />
                            {view === viewsIdentification && (
                                <RequestTabContent
                                    {...props}
                                    {...formProps}
                                    contextProps={contextProps}
                                    loading={loading}
                                    hasLine={hasLine}
                                    permissionaryData={permissionaryData}
                                    companyData={companyData}
                                />
                            )}
                            {view === viewsScheduleBoard && (
                                <ScheduleTab
                                    {...props}
                                    {...formProps}
                                    loading={loading}
                                    contextProps={contextProps}
                                    classes={classes}
                                    isStripContext={isStripContext}
                                    permissionaries={permissionaryData}
                                />
                            )}

                            {view === viewsDocumentation && (
                                <Documentation {...props} {...formProps} contextProps={contextProps} />
                            )}
                            <SaveButton
                                mainColor={mainColor}
                                hideActionBtn={hideActionBtn}
                                isInternalForm={isInternalForm}
                                canEdit={canEdit}
                                onCancel={onCancel}
                                onSave={() => {
                                    if (validateOnSave) {
                                        formProps.validateForm().then((res) => {
                                            if (Object.keys(res).length <= 0) onSave(formProps.values);
                                            else {
                                                notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                    toastId: 'VALIDATION',
                                                    type: 'error',
                                                });
                                            }
                                        });
                                    } else {
                                        let values = {
                                            ...formProps.values,
                                        };
                                        onSave(values);
                                    }
                                }}
                            />
                            <FinalizeButton
                                mainColor={mainColor}
                                hideFinalizeBtn={hideFinalizeBtn}
                                isInternalForm={isInternalForm}
                                canEdit={canEdit}
                                onSubmit={() => {
                                    validateForm && validateForm(formProps.validateForm());
                                }}
                            />
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
}

export default memo(injectIntl(LinesRegistration));
